/* @flow */
/* eslint-env es6,browser */

import * as u from './utils';
import adrotation from './adrotation';
import adconfig from './adconfig';

/**
 * @module bidmanager
 */
/**
 * @description Wrapper to invoke and handle all header bidders
 * @requires module:utils
 */
class _bidManager {
  /*
  - Initialize all supported header bidder object and add to bidders array
  bidders[KEY] - KEY should match the same one in OAS x61 config
  - During each query, only the required one from this object will be added
  to activeBidders array and used
  - Each bidder object should comply with the common public interface (Refer A9 bidder)
  */
  _bidders: {
    [string]: hbPartnerBase
  };
  _activeBidders: Array<hbPartnerBase>;
  _isDialup: boolean;
  _site: string;
  _defConfig: hbBiddersData;
  _config: hbBiddersData;
  _firstLoad: boolean;
  constructor() {
    this._bidders = {};
    this._defConfig = {
      bidders: [],
      bidders_dialup: [],
      shuffleBidders: false,
      timeout: 3000 //default timeout for all bidders put together
    };
    this._config = this._defConfig;
    this._activeBidders = [];
    this._isDialup = false;
    this._site = '';
    this._firstLoad = true;
  }

  /**
   * Initialize the object
   * @param {globalParams} g - globalParams object
   * @returns {undefined}
   */
  init(g: globalParam) {
    this._isDialup = u.getEvaluatedDialup(g.isDialup);
    this._site = g.phInfo.site;
  }

  /**
   * Add Bidder
   * @param {string} name - Biddler name
   * @param {hbPartnerBase} bidder - Bidder Object
   * @returns {undefined}
   */
  addBidder(name: string, bidder: hbPartnerBase) {
    this._bidders[name] = bidder;
  }

  /**
   * get appropriate bidders array based in dialup flag
   * @access private
   * @returns {Array<string>} - List of bidders based on dial-up check
   */
  _getBidders(): Array<string> {
    if (this._isDialup && this._config.bidders_dialup.length) {
      return this._config.bidders_dialup;
    }
    return this._config.bidders;
  }

  /**
   * @global
   * @typedef {a9Config|adtechConfig|indexConfig|sonobiConfig|sovrnConfig|brealConfig|adkernelConfig|criteoConfig} partnerConfig
   */
  /**
   * This function should be called after we get x61 config
   * - Stores the config locally
   * - Pick active bidders based on config.bidders
   * - Configure each active bidders with bidder specific config
   * @param {Object.<string, partnerConfig>} oConfig - config for all bidders from adconfig
   * @returns {undefined}
   */
  updateConfig(oConfig: hbBiddersData) {
    oConfig = oConfig || {};
    this._config = u.extend(true, {}, this._defConfig, oConfig);

    //empty active bidder object and build again as per updated config
    this._activeBidders.length = 0;
    this._activeBidders = this._getBidders()
      .filter(v =>
        //select only the bidder who has valid handler with us
        u.isObject(this._bidders[v])
      )
      .map(v => {
        //Update bidder object and store it as active bidder
        let bidObj = this._bidders[v],
          bidConfig = this._config[v] || {};
        bidObj.updateConfig(bidConfig);
        return bidObj;
      });
  }

  /**
   * Get list of header bid positions.
   * This gets bid positions supported by each/active bidders, concat
   * and return unique.
   * @param {string} pName - page name
   * @return {Array<string>} - List of HB enabled positions
   */
  getBidPos(pName: string): Array<string> {
    pName = pName || '';
    if (!pName.length) {
      return [];
    }
    return u.unique(
      this._activeBidders
        .map(o =>
          //get bid positions supported by the bidder
          o.getBidPositions(pName)
        )
        .reduce((a, b) => a.concat(b), [])
    );
  }

  /**
   * @global
   * @typedef hbPosData
   * @property {string} info=<E>
   * @property {Object} _QP - bid value(cpm) are populated here
   * @property {Object} _CV - partner specific pos id
   */
  /**
   * Prepare default/normalized response for each bid position
   * When we don't get any response from bidder/no bidding available
   * this will be the data returned
   * @param {Array<string>} posList - ad positions
   * @return {Object.<string, hbPosData>} hbPartnerData
   * @access private
   */
  _getDefResponse(posList: Array<string>): hbPartnerData {
    var ret = {},
      _defObj = { info: '<E>', _QP: {}, _CV: {} };
    posList = posList || [];
    if (!u.isArray(posList)) {
      return ret;
    }
    posList.forEach(v => {
      ret[v] = {};
      u.extend(true, ret[v], _defObj);
    });
    return ret;
  }

  /**
   * Prepare bid response for each bid position with zero bid
   * When we don't get any response from bidder/no bidding available
   * this will be the data returned
   * @param {Array<string>} posList - ad positions
   * @param {Object} partner - partner object
   * @return {Object.<string, hbPosData>} hbPartnerData
   * @access private
   * @access private
   */
  _initBidResponse(
    posList: Array<string>,
    partner: hbPartnerBase
  ): hbPartnerData {
    var ret = {},
      _defObj = { info: '', _QP: {}, _CV: {} };
    posList = posList || [];
    if (!u.isArray(posList)) {
      return ret;
    }
    posList.forEach(v => {
      ret[v] = {};
      u.extend(true, ret[v], _defObj);
      ret[v]._QP[partner.getQPKey(v)] = 0;
      ret[v].info = `${partner.getLineId(v)}|0`;
    });
    return ret;
  }

  /**
   * This performs the actual header bidding. Hence bidding needs to be done async.
   * the response here is promise. Caller needs to wait for the response and
   * handle success/failure via then method.
   * Return object will be like {'<POS>': {},'<POS2>':{}}
   * Each position value will contain extra Query Params & custom vars (Designed based on A9
   * bidding and may change in future)
   * @param {Array<string>} posList - ad positions
   * @param {string} pName - pageName
   * @return {Promise<Object.<string, hbPosData>>} hbPartnerData
   */
  getBids(posList: Array<string>, pName: string): Promise<hbPartnerData> {
    let ret = this._getDefResponse(posList),
      pArray = [],
      fl = this._firstLoad;
    posList = posList || [];
    pName = pName || '';
    if (!u.isArray(posList) || !posList.length || !pName.length) {
      return Promise.resolve(ret);
    }

    let startTime = new Date().getTime();

    //send bid request to the bidders who support these positions
    let bidders = this._config.shuffleBidders
      ? u.shuffle(this._activeBidders)
      : this._activeBidders;
    bidders.forEach(v => {
      //get bid positions supported by this bidder (If NONE, no need to query this bidder)
      let bidArray = u.intersect(posList, v.getBidPositions(pName));
      if (bidArray.length) {
        //let's wait with timeout for each bidder (so that we can use resolved bids in case of someone times out)
        pArray.push({
          name: v.getName(),
          promise: Promise.race([
            v.getBids(bidArray, this._initBidResponse(bidArray, v)),
            u.getTimeoutPromise(this._config.timeout, v.getName())
          ])
        });
      }
    });
    if (!pArray.length) {
      return Promise.resolve(ret);
    }

    this._firstLoad = false;

    //Resolve all accumulated promise here
    return u.allSettledTimedPromise(pArray).then(
      arr => {
        let sValues = arr
            .filter(v => v.state === 'fulfilled')
            .map(v => v.value),
          fValues = arr.filter(v => v.state !== 'fulfilled').map(v => v.reason);

        //Log all failed promise
        if (fValues.length) {
          u.logError('Header bidding failed with error ' + fValues.join(','));
        }

        //get updated value from bidder response for each position
        posList.forEach(pos => {
          ret[pos] = sValues
            .filter(v => u.isObject(v[pos])) //filter only bidders who has result for this position
            .map(v => v[pos]) //extract result from bidder
            .reduce((p, c) => {
              //concatenate and send it. Let server decide which bid to use
              if (p.info === '<E>') {
                p.info = '';
              }
              if (p.info.length) {
                p.info += ';';
              }
              p.info += c.info;

              u.extend(true, p._QP, c._QP);
              u.extend(true, p._CV, c._CV);
              return p;
            }, ret[pos]);
        });
        let elapsedTime = new Date().getTime() - startTime;
        let perPartnertt = arr.map(v => `${v.name}:${v.tt}`).join('|');
        u.logInfo(`bids=${posList.map(v => v + ':' + ret[v].info).join(',')}&\
tt=${elapsedTime}|${perPartnertt}&\
du=${this._isDialup ? 1 : 0}&\
site=${this._site}&\
pn=${adconfig.getPageName()}&\
fl=${fl ? 1 : 0}&\
ar=${adrotation.getRotationStatus() ? 1 : 0}`);
        return ret;
      },
      error => {
        u.logError('Header bidding failed: ' + error || '');
        return ret;
      }
    );
  }

  /**
   * render header bidding partner ad
   * @param {window} win - window object
   * @param {window.document} doc - document object
   * @param {string} pos - ad position
   * @param {string} partner - partner name
   * @param {string} param - Extra param (may vary for each bidding partner)
   * @returns {undefined}
   */
  renderAd(
    win: window,
    doc: window.document,
    pos: string,
    partner: string,
    param: string
  ) {
    if (!this._bidders[partner] || !win || win === win.top) {
      u.logError(
        `renderAd failed p=${partner} win:${
          !win || win === win.top ? 'invalid' : 'valid'
        }`
      );
      return;
    }
    let newwin = win;
    //if ad is rendered inside a new iframe created inside our ad iframe
    if ((!pos || !param) && win.parent !== win.top) {
      do {
        newwin = win.parent;
      } while (newwin.parent !== win.top && !newwin.UOL_POS);
      pos = newwin.UOL_POS;
      param = newwin[this._bidders[partner].getCVar()];
    }
    this._bidders[partner].renderAd(win, doc, pos, param);
    let qp = newwin['_QP'],
      bid = 0,
      pagename = newwin['UOL_PAGENAME'] || 'default';
    let ar = newwin['UOL_ADROTATE'] || 0;
    if (u.isObject(qp)) {
      let qpKey = this._bidders[partner].getQPKey(pos);
      if (qpKey && qp[qpKey]) {
        bid = qp[qpKey];
      }
    }
    if (this._bidders[partner]._doReporting()) {
      u.logInfo(`renderAd:\
p=${partner}&\
pos=${pos}&\
bid=${bid}&\
site=${this._site}&\
lid=${this._bidders[partner].getLineId(pos)}&\
pn=${pagename}&\
ar=${ar ? 1 : 0}`);
    }
  }
}
//export singleton object
export default new _bidManager();
